<template>
  <div class="pc-login-container">
    <div class="brand-section">
      <div class="brand-logo">
        <img src="/img/study.png" alt="品牌logo" />
      </div>
      <p class="brand-slogan">让学习更简单，让转学更轻松</p>
      <div class="brand-image">
        <img src="/img/01.jpg" alt="品牌形象" />
      </div>
    </div>

    <div class="login-form-section">
      <div class="form-card">
        <h2 class="form-title">欢迎登录</h2>
        <p class="form-subtitle">请使用您的账户信息登录</p>

        <form class="login-form" @submit.prevent="handleSubmit" autocomplete="off">
          <template v-for="field in schema.fields">
            <div
              :key="field.modelKey"
              class="form-group"
              :class="{
                'has-error': errors[field.modelKey],
                'is-focused': focusedField === field.modelKey
              }"
            >
              <label class="form-label" :for="'field-' + field.modelKey" style="text-align: left; margin-bottom: 10px">
                {{ field.label }}
                <span v-if="field.rules.required" class="required-mark">*</span>
              </label>
              <div class="input-wrapper">
                <input
                  :id="'field-' + field.modelKey"
                  v-model="localModel[field.modelKey]"
                  :type="getFieldType(field)"
                  :placeholder="field.props.placeholder"
                  class="form-input"
                  :autocomplete="field.modelKey === 'password' ? 'current-password' : 'username'"
                  @focus="focusedField = field.modelKey"
                  @blur="onBlur(field)"
                  @input="onInput"
                />
                <button
                  v-if="field.props.type === 'password'"
                  type="button"
                  class="password-toggle"
                  tabindex="-1"
                  :aria-label="showPassword[field.modelKey] ? '隐藏密码' : '显示密码'"
                  @click="togglePassword(field.modelKey)"
                >
                  <svg v-if="!showPassword[field.modelKey]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
              <transition name="slide-fade">
                <span v-if="errors[field.modelKey]" class="error-message">{{ errors[field.modelKey] }}</span>
              </transition>
            </div>
          </template>

          <div class="form-actions">
            <button type="submit" class="login-btn">
              <span class="btn-text">登 录</span>
            </button>
          </div>
        </form>

        <div class="form-footer">
          <p>&copy; 2025 ShangAn. All rights reserved.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPC',
  props: {
    model: { type: Object, required: true },
    schema: { type: Object, required: true }
  },
  data() {
    return {
      localModel: { ...this.model },
      errors: {},
      showPassword: {},
      focusedField: null
    };
  },
  watch: {
    model: {
      handler(val) {
        this.localModel = { ...val };
      },
      deep: true
    }
  },
  methods: {
    getFieldType(field) {
      if (field.props.type === 'password') {
        return this.showPassword[field.modelKey] ? 'text' : 'password';
      }
      return field.props.type || 'text';
    },

    togglePassword(key) {
      this.$set(this.showPassword, key, !this.showPassword[key]);
    },

    onInput() {
      this.$emit('input', { ...this.localModel });
      if (this.errors[this.focusedField]) {
        this.errors[this.focusedField] = '';
      }
    },

    onBlur(field) {
      this.focusedField = null;
      this.validateField(field);
    },

    async validateField(field) {
      const { modelKey, rules, messages } = field;
      const value = (this.localModel[modelKey] || '').toString();

      this.errors = { ...this.errors, [modelKey]: '' };

      if (rules.required && !value.trim()) {
        this.errors[modelKey] = (messages && messages.required) || '此项为必填项';
        return false;
      }

      if (!value.trim()) return true;

      if (rules.min && value.length < rules.min) {
        this.errors[modelKey] = (messages && messages.min) || `最少输入${rules.min}个字符`;
        return false;
      }
      if (rules.max && value.length > rules.max) {
        this.errors[modelKey] = (messages && messages.max) || `最多输入${rules.max}个字符`;
        return false;
      }

      for (const ruleName of Object.keys(rules)) {
        if (['required', 'min', 'max'].includes(ruleName)) continue;
        const validator = rules[ruleName];
        if (typeof validator === 'function') {
          try {
            const asyncValidator = validator(value);
            if (typeof asyncValidator === 'function') {
              const result = await new Promise(resolve => asyncValidator(resolve));
              if (result === false) {
                this.errors[modelKey] = (messages && messages[ruleName]) || '校验失败';
                return false;
              }
              if (typeof result === 'string') {
                this.errors[modelKey] = result;
                return false;
              }
            }
          } catch (e) {
            this.errors[modelKey] = (messages && messages[ruleName]) || '校验异常';
            return false;
          }
        }
      }

      return true;
    },

    async validate() {
      const results = await Promise.all(
        this.schema.fields.map(f => this.validateField(f))
      );
      return results.every(Boolean);
    },

    handleSubmit() {
      this.$emit('login');
    }
  }
};
</script>

<style scoped>
.pc-login-container {
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fa;
  width: 100%;
}

.brand-section {
  flex: 1;
  background: linear-gradient(135deg, #e53935 0%, #f44336 100%);
  color: white;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.brand-logo img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  margin-bottom: 30px;
}

.brand-slogan {
  font-size: 18px;
  opacity: 0.9;
  margin-bottom: 40px;
  max-width: 400px;
  line-height: 1.6;
}

.brand-image img {
  max-width: 80%;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

.login-form-section {
  flex: 0 0 420px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.form-card {
  width: 100%;
  background: #fff;
  border-radius: 16px;
  padding: 40px 36px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.06);
}

.form-title {
  font-size: 26px;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 6px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.form-subtitle {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-bottom: 32px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #444;
  margin-bottom: 6px;
  transition: color 0.2s;
}

.is-focused .form-label {
  color: #e53935;
}

.required-mark {
  color: #e53935;
  margin-left: 2px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  font-size: 15px;
  color: #333;
  background: #fafafa;
  transition: all 0.25s ease;
  outline: none;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #e53935;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(229,57,53,0.08);
}

.form-input:hover:not(:focus) {
  border-color: #ccc;
  background: #fcfcfc;
}

.form-input::placeholder {
  color: #b0b0b0;
  font-size: 14px;
}

.has-error .form-input {
  border-color: #e53935;
  background: #fff5f5;
}

.has-error .form-input:focus {
  box-shadow: 0 0 0 4px rgba(229,57,53,0.12);
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.password-toggle:hover {
  color: #e53935;
  background: rgba(229,57,53,0.06);
}

.error-message {
  display: block;
  font-size: 12px;
  color: #e53935;
  margin-top: 4px;
  padding-left: 2px;
}

.slide-fade-enter-active {
  transition: all 0.25s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.form-actions {
  margin-top: 28px;
}

.login-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #e53935 0%, #d32f2f 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 16px rgba(229,57,53,0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 2px;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(229,57,53,0.35);
}

.login-btn:active {
  transform: translateY(0);
}

.form-footer {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #f0f0f0;
  text-align: center;
  font-size: 12px;
  color: #bbb;
}
</style>
